import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { VxSwal } from '@voxie/frontend-components';
import { createGlobalState } from '@vueuse/core';
import { computed, inject, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { agentsClient, messagesClient } from '~/services';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { MAX_SELECTED_THREADS_NUMBER } from '~/constants/messageHub';

export const useMessageHub = createGlobalState(() => {
    const toasted = useToasted();
    const FEATURES = inject('FEATURES');
    const teamId = Spark.state.currentTeam.id;
    const { onCorporate, getCurrentGroupId } = useCurrentGroup();

    const route = useRoute();

    const isScheduledRoute = computed(() => {
        return ['message-hub.scheduled-messages', 'message-hub.scheduled-message'].includes(route.name);
    });

    /**
     * Used to get the current route.query.inbox, if the user has contamined the
     * query parameter it will default to the appropriate inbox
     */
    const currentInbox = computed(() => {
        if (FEATURES.simple_message_hub) {
            // simple message hub only has "all"
            return 'all';
        }

        // if not valid return the default: "me"
        if (!['me', 'unassigned', 'open', 'closed', 'all'].includes(route.query.inbox)) {
            return 'me';
        }

        return route.query.inbox;
    });

    const hasPhoneNumber = ref();

    const checkPhoneNumber = async () => {
        if (hasPhoneNumber.value === null || hasPhoneNumber.value === true) {
            return;
        }

        hasPhoneNumber.value = null;
        try {
            if (onCorporate()) {
                await messagesClient.getPhoneNumber(teamId);
            } else {
                await messagesClient.getGroupPhoneNumber(teamId, getCurrentGroupId());
            }

            hasPhoneNumber.value = true;
        } catch (error) {
            hasPhoneNumber.value = false;
            if (error?.response?.status === HTTP_NOT_FOUND) {
                VxSwal.fire({
                    title: 'Missing Phone Number',
                    text: 'No phone numbers are available to send messages.',
                    icon: faTriangleExclamation,
                    iconColor: 'warning',
                });
            } else {
                console.error(error);
            }
        }
    };

    const agents = ref([]);

    const getAgents = async () => {
        const response = await agentsClient.getAgents({ teamId, groupId: getCurrentGroupId() });

        agents.value = response.data;
    };

    // Thread selection state - use Map to store complete thread objects
    const selectedThreads = ref(new Map());

    const toggleThreadSelection = (thread) => {
        if (selectedThreads.value.has(thread.contact_id)) {
            selectedThreads.value.delete(thread.contact_id);
            return;
        }

        if (selectedThreads.value.size >= MAX_SELECTED_THREADS_NUMBER) {
            toasted.error(`You can select a maximum of ${MAX_SELECTED_THREADS_NUMBER} threads at once.`);
            return;
        }
        selectedThreads.value.set(thread.contact_id, thread);
    };

    const clearSelection = () => {
        selectedThreads.value.clear();
    };

    const removeThreadSelection = (thread) => {
        selectedThreads.value.delete(thread.contact_id);
    };

    const isThreadSelected = (threadId) => {
        return selectedThreads.value.has(threadId);
    };

    // Watch route changes to clear selection when leaving bulk-actions
    watch(
        () => route.name,
        () => {
            if (route.name !== 'message-hub.bulk-actions') {
                clearSelection();
            }
        }
    );

    return {
        currentInbox,

        isScheduledRoute,

        checkPhoneNumber,
        hasPhoneNumber,

        agents,
        getAgents,

        // Thread selection
        selectedThreads,
        toggleThreadSelection,
        isThreadSelected,
        clearSelection,
        removeThreadSelection,
        MAX_SELECTED_THREADS_NUMBER,
    };
});
